package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronLeft
import com.composables.icons.lucide.ChevronRight
import com.composables.icons.lucide.Lucide
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_period_from
import portalvv.shared.generated.resources.operational_period_mode_custom
import portalvv.shared.generated.resources.operational_period_mode_month
import portalvv.shared.generated.resources.operational_period_mode_total
import portalvv.shared.generated.resources.operational_period_mode_year
import portalvv.shared.generated.resources.operational_period_to

private const val MIN_YEAR = 2000

@Composable
fun PeriodSelector(modifier: Modifier = Modifier) {
    val period = LocalGlobalPeriod.current
    PeriodSelector(
        selection = period.selection,
        onMode = period::setMode,
        onMonth = period::selectMonth,
        onYear = period::selectYear,
        onCustom = period::selectCustom,
        modifier = modifier,
    )
}

@Composable
fun PeriodSelector(
    selection: PeriodSelection,
    onMode: (PeriodMode) -> Unit,
    onMonth: (MonthPeriod) -> Unit,
    onYear: (Int) -> Unit,
    onCustom: (MonthPeriod, MonthPeriod) -> Unit,
    modifier: Modifier = Modifier,
    bordered: Boolean = true,
    modes: List<PeriodMode> = listOf(PeriodMode.MONTH, PeriodMode.YEAR, PeriodMode.TOTAL, PeriodMode.CUSTOM),
    modeLabels: Map<PeriodMode, StringResource> = emptyMap(),
) {
    val current = remember { SystemDateTimeProvider().currentPeriod() }
    val monthOptions = remember { (1..12).map { MonthCalendar.monthName(it) } }
    val yearOptions = remember(current.year) { (MIN_YEAR..current.year + 5).map { it.toString() } }

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = if (bordered) BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant) else null,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            ModeSegmented(mode = selection.mode, options = modes, labels = modeLabels, onSelect = onMode)
            when (selection.mode) {
                PeriodMode.MONTH -> {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                        IconButton(onClick = { onMonth(selection.month.previous()) }) {
                            Icon(Lucide.ChevronLeft, contentDescription = null)
                        }
                        Text(
                            text = MonthCalendar.label(selection.month),
                            style = MaterialTheme.typography.titleMedium,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.widthIn(min = 150.dp),
                        )
                        IconButton(onClick = { onMonth(selection.month.next()) }) {
                            Icon(Lucide.ChevronRight, contentDescription = null)
                        }
                    }
                }
                PeriodMode.YEAR -> {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                        IconButton(onClick = { onYear(selection.year - 1) }) {
                            Icon(Lucide.ChevronLeft, contentDescription = null)
                        }
                        Text(
                            text = selection.year.toString(),
                            style = MaterialTheme.typography.titleMedium,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.widthIn(min = 90.dp),
                        )
                        IconButton(onClick = { onYear(selection.year + 1) }) {
                            Icon(Lucide.ChevronRight, contentDescription = null)
                        }
                    }
                }
                PeriodMode.TOTAL -> Unit
                PeriodMode.CUSTOM -> {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                        RangeSide(
                            label = stringResource(Res.string.operational_period_from),
                            selected = selection.start,
                            monthOptions = monthOptions,
                            yearOptions = yearOptions,
                            onSelect = { start -> onCustom(start, if (start.index() > selection.end.index()) start else selection.end) },
                        )
                        RangeSide(
                            label = stringResource(Res.string.operational_period_to),
                            selected = selection.end,
                            monthOptions = monthOptions,
                            yearOptions = yearOptions,
                            onSelect = { end -> onCustom(if (end.index() < selection.start.index()) end else selection.start, end) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun MonthPeriodSelector(
    month: MonthPeriod,
    onMonthChange: (MonthPeriod) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
    ) {
        IconButton(onClick = { onMonthChange(month.previous()) }) {
            Icon(Lucide.ChevronLeft, contentDescription = null)
        }
        Text(
            text = MonthCalendar.label(month),
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
            modifier = Modifier.widthIn(min = 150.dp),
        )
        IconButton(onClick = { onMonthChange(month.next()) }) {
            Icon(Lucide.ChevronRight, contentDescription = null)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ModeSegmented(
    mode: PeriodMode,
    options: List<PeriodMode>,
    labels: Map<PeriodMode, StringResource>,
    onSelect: (PeriodMode) -> Unit,
) {
    SingleChoiceSegmentedButtonRow {
        options.forEachIndexed { index, option ->
            SegmentedButton(
                selected = mode == option,
                onClick = { onSelect(option) },
                shape = SegmentedButtonDefaults.itemShape(index = index, count = options.size),
            ) {
                Text(
                    text = modeLabel(option, labels),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    }
}

@Composable
private fun RangeSide(
    label: String,
    selected: MonthPeriod,
    monthOptions: List<String>,
    yearOptions: List<String>,
    onSelect: (MonthPeriod) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
            CompactDropdown(
                options = monthOptions,
                selected = MonthCalendar.monthName(selected.month),
                onSelect = { name -> onSelect(selected.copy(month = monthOptions.indexOf(name) + 1)) },
                modifier = Modifier.width(150.dp),
            )
            CompactDropdown(
                options = yearOptions,
                selected = selected.year.toString(),
                onSelect = { value -> value.toIntOrNull()?.let { onSelect(selected.copy(year = it)) } },
                modifier = Modifier.width(100.dp),
            )
        }
    }
}

@Composable
private fun CompactDropdown(
    options: List<String>,
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = modifier) {
        Surface(
            shape = AppShapes.medium,
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            modifier = Modifier.fillMaxWidth().clickable { expanded = true },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Text(
                    text = selected,
                    style = MaterialTheme.typography.bodyMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                Icon(Lucide.ChevronDown, contentDescription = null, modifier = Modifier.size(16.dp))
            }
        }
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option) },
                    onClick = {
                        expanded = false
                        onSelect(option)
                    },
                )
            }
        }
    }
}

@Composable
private fun modeLabel(
    mode: PeriodMode,
    labels: Map<PeriodMode, StringResource>,
): String =
    labels[mode]?.let { stringResource(it) } ?: when (mode) {
        PeriodMode.MONTH -> stringResource(Res.string.operational_period_mode_month)
        PeriodMode.YEAR -> stringResource(Res.string.operational_period_mode_year)
        PeriodMode.TOTAL -> stringResource(Res.string.operational_period_mode_total)
        PeriodMode.CUSTOM -> stringResource(Res.string.operational_period_mode_custom)
    }

private fun MonthPeriod.index(): Int = year * 12 + month
